import { useState, useCallback, useRef, useEffect } from 'react';

interface MessageUpdate {
  id: string;
  [key: string]: any;
}

export const useDebouncedMessageUpdates = <T extends MessageUpdate>(delay: number = 300) => {
  const pendingUpdatesRef = useRef<Map<string, T>>(new Map());
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const appliedUpdatesRef = useRef<Set<string>>(new Set());
  const [updateTrigger, setUpdateTrigger] = useState(0);

  const scheduleUpdate = useCallback((messageUpdate: T, applyImmediately?: boolean) => {
    const messageId = messageUpdate.id;
    const isFirstUpdate = !appliedUpdatesRef.current.has(messageId);
    
    if (isFirstUpdate || applyImmediately) {
      appliedUpdatesRef.current.add(messageId);
      pendingUpdatesRef.current.set(messageId, messageUpdate);
      setUpdateTrigger(prev => prev + 1);
      return;
    }

    pendingUpdatesRef.current.set(messageId, messageUpdate);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setUpdateTrigger(prev => prev + 1);
    }, delay);
  }, [delay]);

  const getPendingUpdates = useCallback(() => {
    const updates = Array.from(pendingUpdatesRef.current.values());
    pendingUpdatesRef.current.clear();
    return updates;
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { scheduleUpdate, getPendingUpdates, updateTrigger };
};
